import { useState, useEffect, useCallback, forwardRef, useRef, ChangeEvent } from 'react';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import { FaSearch, FaMagic } from 'react-icons/fa';
import { BiImage, BiCamera } from 'react-icons/bi';

import { Modal, ModalMethods, Text, Loader, toasts } from '../../../../../ui-component';
import { api } from '../../../../../utils/apiRequest';
import { getValueFromLocalStorage } from '../../../../../utils/common';

export type StitchOptionIconType = {
  id?: number;
  name?: string;
  image_url?: string;
  short_lived_url?: string;
  reference_id?: string;
};

type StitchOptionIconSelectorModalProps = {
  onIconSelect: (icon: StitchOptionIconType) => void;
  selectedIconRefId?: string;
};

const StitchOptionIconSelectorModal = forwardRef<ModalMethods, StitchOptionIconSelectorModalProps>(
  ({ onIconSelect, selectedIconRefId }, ref) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [allIcons, setAllIcons] = useState<StitchOptionIconType[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const fetchIcons = useCallback(async () => {
      setIsLoading(true);

      try {
        const boutiqueId = getValueFromLocalStorage('boutique_id');
        const headers: Record<string, string> = {
          Accept: 'application/json',
        };

        if (boutiqueId !== null && boutiqueId !== undefined && boutiqueId !== '') {
          headers['x-boutique-id'] = String(boutiqueId);
        }

        const response = await api.getRequest('new-stitch-option/icons', headers);

        if (response.status && Array.isArray(response.data)) {
          const mappedIcons: StitchOptionIconType[] = (
            response.data as Array<{
              id?: number | null;
              label?: string;
              url?: string | null;
              reference_id?: string;
            }>
          ).map((icon) => ({
            id: icon.id ?? undefined,
            name: icon.label,
            image_url: icon.url ?? undefined,
            short_lived_url: icon.url ?? undefined,
            reference_id: icon.reference_id ?? '',
          }));

          setAllIcons(mappedIcons);
        } else {
          setAllIcons([]);
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'fetch-stitch-icons-error');
        }
        setAllIcons([]);
      } finally {
        setIsLoading(false);
      }
    }, []);

    const icons = _isEmpty(searchQuery.trim())
      ? allIcons
      : allIcons.filter((icon) =>
          (icon.name || '').toLowerCase().includes(searchQuery.trim().toLowerCase())
        );

    useEffect(() => {
      void fetchIcons();
    }, [fetchIcons]);

    const handleIconClick = (icon: StitchOptionIconType) => {
      const referenceId =
        icon.reference_id ||
        (icon.image_url ? icon.image_url.split('/').pop() || '' : '') ||
        (icon.id ? String(icon.id) : '');

      onIconSelect({
        ...icon,
        reference_id: referenceId,
      });

      if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
        ref.current.hide();
      }
    };

    const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;

      if (!_isNil(files) && files[0]) {
        setIsUploading(true);

        try {
          const formDataUpload = new FormData();
          const file = files[0];
          const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '') || 'stitch-option';

          formDataUpload.append('file', file);
          formDataUpload.append('file_type', '9');
          formDataUpload.append('name', nameWithoutExt);

          const response = await api.postRequest(
            'storage/upload_multiple_files',
            formDataUpload,
            true
          );

          if (response.status && !_isNil(response.data) && response.data.response?.length > 0) {
            const uploadedImage = response.data.response[0];
            const referenceId =
              uploadedImage.reference_id || uploadedImage.image_link || uploadedImage.name;

            const iconData: StitchOptionIconType = {
              id: uploadedImage.id,
              name: nameWithoutExt,
              image_url: uploadedImage.short_lived_url || uploadedImage.icon_url,
              short_lived_url: uploadedImage.short_lived_url,
              reference_id: referenceId,
            };

            onIconSelect(iconData);

            if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
              ref.current.hide();
            }
          } else {
            toasts('error', 'Failed to upload option icon', 'stitch-icon-upload-error');
          }
        } catch (err) {
          if (err instanceof Error) {
            toasts('error', err.message, 'stitch-icon-upload-error');
          }
        } finally {
          setIsUploading(false);

          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      }
    };

    const handleAddFromGallery = () => {
      fileInputRef.current?.click();
    };

    const handleCamera = () => {
      toasts('info', 'Camera feature coming soon', 'stitch-icon-camera');
    };

    const handleCreateWithAI = () => {
      toasts('info', 'AI creation coming soon', 'stitch-icon-ai');
    };

    return (
      <Modal
        ref={ref}
        size="xl"
        title="Select, Upload or create an icon"
        showFooter={false}
        showCloseIcon={true}
      >
        <div className="flex flex-col h-full min-h-[400px]">
          <div className="mb-4">
            <Text className="font-inter font-semibold text-sm mb-2 block" color="black">
              Select an icon
            </Text>
            <div className="relative flex items-center w-full">
              <FaSearch className="absolute left-3 text-gray-500 pointer-events-none" size={16} />
              <input
                type="text"
                placeholder="Search by item name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-10 pl-10 pr-3 border border-gray-300 rounded-lg text-sm font-inter outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500"
              />
            </div>
          </div>

          {isLoading || isUploading ? (
            <div className="flex justify-center items-center h-64">
              <Loader />
            </div>
          ) : (
            <>
              {icons.length > 0 ? (
                <div
                  className="flex flex-wrap gap-3 py-4 px-2 max-h-[400px] overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-track]:rounded [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:hover:bg-gray-500"
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#888 #f1f1f1',
                  }}
                >
                  {icons.map((icon, index) => {
                    const iconId = icon.id ?? icon.reference_id ?? index;
                    const iconUrl = icon.image_url || icon.short_lived_url || '';
                    const isSelected = !_isEmpty(selectedIconRefId)
                      ? String(selectedIconRefId) === String(icon.reference_id) ||
                        String(selectedIconRefId) === String(iconId)
                      : false;

                    return (
                      <div
                        key={iconId}
                        onClick={() => handleIconClick(icon)}
                        className={`relative w-20 h-20 min-w-[80px] aspect-square rounded-lg bg-white cursor-pointer flex items-center justify-center overflow-hidden transition-all hover:border-blue-500 hover:scale-105 hover:shadow-md ${
                          isSelected
                            ? 'border-2 border-blue-500'
                            : 'border-2 border-gray-300 hover:border-blue-500'
                        }`}
                      >
                        <img
                          src={iconUrl}
                          alt={icon.name || 'Stitch option icon'}
                          className="w-full h-full object-contain p-2"
                        />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex justify-center items-center h-[200px] p-5">
                  <Text color="gray" size="small">
                    {searchQuery ? 'No icons found matching your search' : 'No icons available'}
                  </Text>
                </div>
              )}
            </>
          )}

          <div className="flex gap-4 py-4 border-t border-gray-300 mt-auto">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png, image/jpeg, image/jpg"
              className="hidden"
              onChange={handleFileUpload}
              disabled={isUploading}
            />
            <button
              type="button"
              onClick={handleAddFromGallery}
              disabled={isUploading}
              className="flex items-center gap-2 px-4 py-2.5 bg-transparent border border-gray-300 rounded-lg cursor-pointer transition-all hover:bg-gray-50 hover:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <BiImage size={20} className="text-black" />
              <Text size="small" color="black">
                {isUploading ? 'Uploading...' : 'Add from gallery'}
              </Text>
            </button>
            <button
              type="button"
              onClick={handleCamera}
              className="flex items-center gap-2 px-4 py-2.5 bg-transparent border border-gray-300 rounded-lg cursor-pointer transition-all hover:bg-gray-50 hover:border-blue-500"
            >
              <BiCamera size={20} className="text-black" />
              <Text size="small" color="black">
                Camera
              </Text>
            </button>
            <button
              type="button"
              onClick={handleCreateWithAI}
              className="flex items-center gap-2 px-4 py-2.5 bg-transparent border border-gray-300 rounded-lg cursor-pointer transition-all hover:bg-gray-50 hover:border-blue-500"
            >
              <FaMagic size={20} className="text-black" />
              <Text size="small" color="black">
                Create with AI
              </Text>
            </button>
          </div>
        </div>
      </Modal>
    );
  }
);

StitchOptionIconSelectorModal.displayName = 'StitchOptionIconSelectorModal';

export default StitchOptionIconSelectorModal;
